import { getDataSourceDisplayName } from '@apps/web/lib/data_source_utils';
import {
    type components,
    type DataSourceType,
} from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

export type AvailabilitySource =
    components['schemas']['CaregiverAvailabilitySource'];
export type SourceLabel = string | null;

export function getAvailabilitySourceLabel(
    source: AvailabilitySource,
    sourceSystem: DataSourceType | null | undefined,
    externalSourceLabel: string | undefined,
): string {
    switch (source) {
        case 'external':
            if (sourceSystem) {
                return getDataSourceDisplayName(sourceSystem);
            }
            return externalSourceLabel ?? 'External';
        case 'llm_tool':
        case 'post_call_llm':
            return 'Phoebe';
        case 'manual':
            return 'Manual';
        default:
            assert.never(source);
    }
}

function getDistinctSourceLabels(
    entries: ReadonlyArray<{ readonly sourceLabel: SourceLabel }>,
): Array<string> {
    return Array.from(
        new Set(
            entries
                .map((entry) => entry.sourceLabel)
                .filter((label): label is string => !!label),
        ),
    );
}

/** Details name every contributing source; compact calendar chips may count them. */
export function getExpandedSourceLabel(
    entries: ReadonlyArray<{ readonly sourceLabel: SourceLabel }>,
): SourceLabel {
    return getDistinctSourceLabels(entries).join(', ') || null;
}

export function getCollapsedSourceLabel(
    entries: ReadonlyArray<{ readonly sourceLabel: SourceLabel }>,
): SourceLabel {
    const labels = getDistinctSourceLabels(entries);
    if (labels.length === 0) {
        return null;
    }
    return labels.length === 1 ? labels[0]! : `${labels.length} sources`;
}

export function getTimeboxedAvailabilitySourcePriority(
    source: AvailabilitySource,
): number {
    switch (source) {
        case 'manual':
            return 0;
        case 'llm_tool':
        case 'post_call_llm':
            return 1;
        case 'external':
            return 2;
        default:
            assert.never(source);
    }
}
